import { Recurrence, RecurrenceConverters, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePane, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import _ from 'underscore';
import { L10n } from '../../ts/L10n/L10n';
import { IRemoteRefreshScheduleInfo } from '../../ts/models/IRemoteRefreshScheduleInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { RemoteRefreshSchedules } from '../../ts/resources/RemoteRefreshSchedules';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import React from 'react';
import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, HostedStackedElementContainer, TooltipPosition, TooltipProps, Tooltip, RadioButtonWithLabelOption, RadioButtonWithLabelGroupWidget, RadioLayoutType } from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { cssClass, Style } from '../../style';
import { imgPath } from '../../common/utilities/ImgUrl';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { SiteUrls } from '@tableau/shared-urls';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { RemoteRefreshTypes } from '../../ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';


export function localizeRefreshTargetType(type: string): string {
  switch(type) {
    case RemoteRefreshTypes.Full:
      return Messages.remoteRefreshScheduleTypeNameFull();
    case RemoteRefreshTypes.Incremental:
      return Messages.remoteRefreshScheduleTypeNameIncremental();
  }
}

export interface EditRemoteRefreshScheduleActionProps {
  editRemoteRefreshPrompt: string;
  recurrence: Recurrence;
  remoteRefreshTypes: string[];
  availableTypeOptions: string[];
  refreshType: string;
  dialogWidth: number;
  close: () => void;
  confirm: (recurrence: Recurrence, refreshType: string) => void;
}

export interface EditRemoteRefreshScheduleActionState {
  recurrence: Recurrence;
  refreshTypeSelectedIndex: number;
}

export class EditRemoteRefreshScheduleAction extends React.Component<EditRemoteRefreshScheduleActionProps, EditRemoteRefreshScheduleActionState> {

  public static execute(item: INamedItemInfo,
      schedule: IRemoteRefreshScheduleInfo,
      availableTypes: string[]): Promise<IResult> {

      return new Promise((resolve, reject) => {
        const hostContainer = new HostedStackedElementContainer();
        const close = () => { hostContainer && hostContainer.remove(); };
        const existingRecurrence = RecurrenceConverters.castScheduleToRecurrence(schedule);
        const props: EditRemoteRefreshScheduleActionProps = {
          editRemoteRefreshPrompt: Messages.editRemoteRefreshScheduleCustomDialogPrompt({DATASOURCE_NAME: L10n.elideIfLong(item.name)}),
          recurrence: existingRecurrence || StaticInitializers.defaultRecurrence(),
          remoteRefreshTypes: Object.values(RemoteRefreshTypes),
          availableTypeOptions: availableTypes,
          refreshType: schedule.refreshType,
          dialogWidth: 550,
          close,
          confirm: (recurrence: Recurrence, refreshType: string) => {
            close();
            const finalSchedule: IRemoteRefreshScheduleInfo = {
              id: schedule.id,
              contentType: schedule.contentType,
              definition: null,
              scheduleDetails: recurrence.recurrenceDetails,
              scheduleType: recurrence.recurrenceType,
              refreshType: refreshType
            };
            RemoteRefreshSchedules.update(item.id, schedule, finalSchedule)
            .then(result => {
              if (result.errors) {
                ToasterService.getInstance().pop({
                  type: ToastType.Error,
                  text: Messages.errorUnknown()
                });
              } else {
                ToasterService.getInstance().pop({
                  type: ToastType.Info,
                  text: Messages.editRemoteRefreshScheduleSuccess()
                });
              }
              resolve(result);
            })
            .catch(error => reject(error));
          }
        };
        const dialog = React.createElement(EditRemoteRefreshScheduleAction, props);
        hostContainer.render(dialog);
      });
  }

  constructor(props: EditRemoteRefreshScheduleActionProps) {
    super(props);
    this.state = {
      recurrence: props.recurrence,
      refreshTypeSelectedIndex: props.remoteRefreshTypes.indexOf(props.refreshType)
    };
  }

  public getRefreshTypeOptions(): RadioButtonWithLabelOption<string>[] {
    return this.props.remoteRefreshTypes.map((type: string) => ({
      value: type,
      testId: `radio-button-${type}`,
      label: localizeRefreshTargetType(type)
    }));
  }

  public renderFooter() {
    return <DialogFooterButtonsWidget
      buttons={[
        {
          handleClick: () => this.props.close(),
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: 'footer-cancel'
        },
        {
          handleClick: () => this.props.confirm (
            this.state.recurrence,
            this.props.remoteRefreshTypes[this.state.refreshTypeSelectedIndex]
          ),
          buttonType: ButtonType.Primary,
          children: Messages.editRemoteRefreshSchedule(),
          testId: 'footer-confirm',
        },
      ]}
      testId="remote-refresh-dialog-footer"
    />;
  }

  public render(): React.ReactNode {
    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: server.sessionSiteUrlName(),
      queryString: window.location.search
    }).userSettingsUrl(server.sessionUserInfo().username, server.sessionUserInfo().domainName);
    const timeZoneDetails = {
      timeZonesEnabled: true,
      timeZoneDisplayName: server.userTimeZone().displayName,
      linkToUserSettings: userAccountSettingsLink
    };
    const baseHeaderStyle: Style = {
      fontWeight: 'normal',
      fontSize: 20,
      marginTop: '0px',
      marginBottom: '18px'
    };
    const basePromptStyle: Style = {
      fontWeight: 'normal',
      fontSize: 13,
      paddingLeft: '12px',
      paddingRight: '12px',
      marginBottom: '12px'
    };
    const dialogHeaderStyle = cssClass('refresh-dialog', 'dialog-header', baseHeaderStyle);
    const promptTextStyle = cssClass('refresh-dialog', 'dialog-prompt', basePromptStyle);
    const tooltipPath = imgPath('info_normal.svg');
    const tooltipProps: TooltipProps = {
      getContent: () => <span>{Messages.editRemoteRefreshScheduleCustomDialogInfoTip()}</span>,
      position: TooltipPosition.Left,
      maxWidth: 200,
    };
    const refreshTypeItems: RadioButtonWithLabelOption<string>[] = this.getRefreshTypeOptions();
    return <DialogWidget
      testId = {'remote-refresh-inner'}
      type={DialogType.NormalWithoutTitleBar}
      onCloseRequest={this.props.close}
      showCloseButton={true}
      positioningBehavior={DialogPositioningBehavior.Draggable}
      initialPosition={{top: 100, left: (window.innerWidth - this.props.dialogWidth)/2}}
      isModal={true}
      showFooter={true}
      handleGlassClick={this.props.close}
      isGlassTransparent={false}
      footerContent={this.renderFooter()}
      dialogWidth={this.props.dialogWidth}
      dialogStyle={{paddingLeft: 12, paddingRight: 12}}
    >
      <div>
        <h1 className={dialogHeaderStyle}>{Messages.editRemoteRefreshScheduleDialogTitle()}</h1>
        <div className={promptTextStyle}>
          <span>{this.props.editRemoteRefreshPrompt}</span>
          <Tooltip {...tooltipProps}>
            <img style={{height: 14, marginLeft: 3, verticalAlign: 'text-top', outlineStyle: 'none' }} src={tooltipPath} alt={''}/>
          </Tooltip>
        </div>
        <div>
          <SelfServiceSchedulePane
            recurrenceModel={this.state && this.state.recurrence}
            recurrenceModelChangedCallback={(recurrence) => this.setState({ recurrence })}
            timeZoneDetails={timeZoneDetails}
            type={SelfServiceSchedulePaneType.BridgeExtractRefresh}
            uncollapsible={false}
            onTimeZoneClick={this.props.close}
          />
        </div>
        <div style={{ marginTop: 30, marginBottom: 10 }}>
          {this.props.availableTypeOptions.length > 1 && <>
          <RadioButtonWithLabelGroupWidget
            label={Messages.refreshSelfServiceScheduleRefreshType()}
            ariaLabel='The type of extract to schedule'
            buttonList={refreshTypeItems}
            checkedIndex={this.state.refreshTypeSelectedIndex}
            handleChange={(_: string, checkedIndex: number) => {
              this.setState({ refreshTypeSelectedIndex: checkedIndex });
            }}
            layout={RadioLayoutType.Veritical}
          />
          </>}
        </div>
      </div>
    </DialogWidget>;
  }
}
